import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { client } from "./generated/client.gen";
import type { HttpError } from "./generated";

client.setConfig({ baseUrl: "/api" });

/** Errors without a JSON body (network failures, the dev proxy's 502) get the API's error shape. */
function toHttpError(error: unknown, response: Response | undefined): HttpError {
  if (error && typeof error === "object" && "statusCode" in error) return error as HttpError;
  const status = response?.status ?? 0;
  const message = typeof error === "string" && error ? error : `Request failed (${status || "network error"})`;
  return { statusCode: status, message };
}

/**
 * Sets up the generated API client: sends the Kinde access token with every request,
 * sends a 401 to sign in and a 403 (email not allowed) to /not-allowed, and gives every
 * error the API's `HttpError` shape. Renders its children once this is in place.
 */
export function ApiClientSetup({ children }: { children: ReactNode }) {
  const { getAccessToken } = useKindeAuth();
  const [, navigate] = useLocation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const onRequest = async (request: Request) => {
      const token = await getAccessToken();
      if (token) request.headers.set("Authorization", `Bearer ${token}`);
      return request;
    };
    const onResponse = (response: Response) => {
      if (response.status === 401) navigate("/sign-in", { replace: true });
      if (response.status === 403) navigate("/not-allowed", { replace: true });
      return response;
    };
    client.interceptors.request.use(onRequest);
    client.interceptors.response.use(onResponse);
    client.interceptors.error.use(toHttpError);
    setReady(true);
    return () => {
      client.interceptors.request.eject(onRequest);
      client.interceptors.response.eject(onResponse);
      client.interceptors.error.eject(toHttpError);
    };
  }, [getAccessToken, navigate]);

  return ready ? children : null;
}
